/**
 * TaxBreakdownWidget — Splits collected tax by product category + by payment
 * method over a rolling window. This is what a real bookkeeper needs at
 * filing time — Clover only shows you one lump sum.
 *
 * Excludes test_sale receipts server-side so you can experiment with the
 * Register Simulator without polluting real reports.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Receipt, Download, PieChart } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const currency = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const pct = (r) => `${((Number(r) || 0) * 100).toFixed(2)}%`;

const PERIODS = [
  { key: 7,   label: "7d" },
  { key: 30,  label: "30d" },
  { key: 90,  label: "Quarter" },
  { key: 365, label: "Year" },
];

export default function TaxBreakdownWidget({ businessSlug, isDark }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/grocery/b/${businessSlug}/tax-breakdown`, { params: { days } });
      setData(r.data);
    } catch { /* silent — empty state renders */ }
    setLoading(false);
  }, [businessSlug, days]);

  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    if (!data) return;
    const rows = [
      ["Category", "Sales", "Tax Collected", "Effective Rate", "Transactions"],
      ...data.by_category.map((r) => [r.category, r.sales, r.tax, r.effective_rate, r.transaction_count]),
      [],
      ["Payment Method", "Sales", "Tax", "Transactions"],
      ...data.by_payment_method.map((r) => [r.payment_method, r.sales, r.tax, r.transaction_count]),
    ];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tax-breakdown-${businessSlug}-${data.start_date}-to-${data.end_date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const cardBg = isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200";
  const heading = isDark ? "text-white" : "text-slate-900";
  const sub = isDark ? "text-slate-400" : "text-slate-500";
  const rowBg = isDark ? "border-slate-700" : "border-slate-200";

  return (
    <Card className={cardBg} data-testid="tax-breakdown-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className={`text-base flex items-center gap-2 ${heading}`}>
            <PieChart className="w-4 h-4" /> Tax Breakdown
          </CardTitle>
          <div className="flex items-center gap-1.5">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setDays(p.key)}
                className={`text-xs px-2 py-1 rounded ${
                  days === p.key
                    ? (isDark ? "bg-cyan-500/20 text-cyan-300" : "bg-cyan-100 text-cyan-800")
                    : (isDark ? "text-slate-400 hover:bg-slate-700" : "text-slate-500 hover:bg-slate-100")
                }`}
                data-testid={`tax-breakdown-period-${p.key}`}
              >
                {p.label}
              </button>
            ))}
            <Button size="sm" variant="outline" onClick={exportCSV} disabled={!data} className="h-7 text-xs">
              <Download className="w-3 h-3 mr-1" /> CSV
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading || !data ? (
          <p className={`text-sm ${sub}`}>Loading tax breakdown…</p>
        ) : (
          <div className="space-y-4">
            {/* Top-level totals */}
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: "Total Sales", value: currency(data.total_sales), color: "text-emerald-500" },
                { label: "Tax Collected", value: currency(data.total_tax_collected), color: "text-cyan-500" },
                { label: "Effective Rate", value: pct(data.effective_rate), color: "text-amber-500" },
              ].map((k, i) => (
                <div key={i} className={`p-3 rounded ${isDark ? "bg-slate-900/40" : "bg-slate-50"}`}>
                  <p className={`text-[10px] uppercase tracking-wide ${sub}`}>{k.label}</p>
                  <p className={`text-lg font-mono font-semibold ${k.color}`}>{k.value}</p>
                </div>
              ))}
            </div>

            {/* By category */}
            <div>
              <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${sub}`}>By Category</p>
              {data.by_category.length === 0 ? (
                <p className={`text-sm ${sub}`}>
                  No categorized sales in this window. Categorize items in Clover (or in the Inventory tab) so we can split tax properly.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="tax-breakdown-by-category">
                    <thead>
                      <tr className={`${sub} text-xs`}>
                        <th className="text-left font-medium p-2">Category</th>
                        <th className="text-right font-medium p-2">Sales</th>
                        <th className="text-right font-medium p-2">Tax</th>
                        <th className="text-right font-medium p-2">Rate</th>
                        <th className="text-right font-medium p-2">Txns</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.by_category.map((r, i) => (
                        <tr key={r.category} className={`border-t ${rowBg}`} data-testid={`tax-cat-${r.category}`}>
                          <td className={`p-2 ${heading}`}>{r.category}</td>
                          <td className={`p-2 text-right font-mono ${sub}`}>{currency(r.sales)}</td>
                          <td className={`p-2 text-right font-mono ${isDark ? "text-cyan-400" : "text-cyan-700"}`}>{currency(r.tax)}</td>
                          <td className={`p-2 text-right font-mono text-xs ${sub}`}>{pct(r.effective_rate)}</td>
                          <td className={`p-2 text-right font-mono text-xs ${sub}`}>{r.transaction_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* By payment method */}
            <div>
              <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${sub}`}>By Payment Method</p>
              {data.by_payment_method.length === 0 ? (
                <p className={`text-sm ${sub}`}>No transactions in this window.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {data.by_payment_method.map((r) => (
                    <div key={r.payment_method} className={`p-2 rounded ${isDark ? "bg-slate-900/40" : "bg-slate-50"}`}>
                      <p className={`text-[10px] uppercase tracking-wide ${sub}`}>{r.payment_method}</p>
                      <p className={`text-sm font-mono font-semibold ${heading}`}>{currency(r.sales)}</p>
                      <p className={`text-[10px] font-mono ${sub}`}>{currency(r.tax)} tax · {r.transaction_count} txns</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {data && (
          <p className={`text-[10px] mt-3 ${sub}`}>
            <Receipt className="inline w-2.5 h-2.5 mr-1" />
            {data.start_date} → {data.end_date} · Excludes test-sale receipts
          </p>
        )}
      </CardContent>
    </Card>
  );
}
